/**
 * Langganan Flow — data yang HANYA server boleh baca (PLAN_FLOW_PRODUK.md Fasa 0–2).
 * Kerja sebenar: functions/flowLangganan.js (callable `adminFlowLangganan`).
 *
 *   ringkasan → Gmail Session Access (Type 1) / Gmail + credit Type 2 bagi setiap langganan
 *   nota      → simpan Notes admin
 *   sejarah   → resit + log Extend / Set date
 *
 * `gabungRingkasan` menentukan STATUS paparan. Keputusan user 30 Sept: tiada lagi
 * konsep "login details belum dihantar" (Type 1 buka sendiri melalui extension,
 * Type 2 auto-login) — jadi status hanya: tiada Gmail · aktif · hampir tamat · tamat.
 */
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';

const panggil = async (data) => (await httpsCallable(functions, 'adminFlowLangganan')(data)).data;
/* Koleksi rekod: 'sub' = flow_subs (Billplz + manual sejak Fasa 3A) · 'tenant' = penyewa
   lama flow_tenants yang belum dipindah. JANGAN teka dari c.manual (itu asal-usul, bukan koleksi). */
export const jenisRekod = (c) => (c.kind === 'tenant' ? 'tenant' : 'sub');
const kunci = (c) => `${jenisRekod(c)}:${c.id}`;

export const muatRingkasanLangganan = async () => (await panggil({ action: 'ringkasan' })).items || {};
export const simpanNotaLangganan = (c, text) => panggil({ action: 'nota', kind: jenisRekod(c), id: c.id, text });
export const muatSejarahLangganan = (c) => panggil({ action: 'sejarah', kind: jenisRekod(c), id: c.id });

/* ── Tulisan (Fasa 3A): SEMUA melalui server — client tidak lagi menulis flow_tenants. ── */
/** Add customer manual. `data`: phone, name, email, accountEmail, jenis, productId, startedAt/expiresAt/paidAt (ms), priceSen, hadKredit, note.
 *  `phone` ATAU `email` — sekurang-kurangnya satu (utils/flowHubungan.js). Jawapan: { ok, id, adaAkaun, via }. */
export const tambahLanggananManual = (data) => panggil({ action: 'tambah', data });
/** Borang Add customer: akaun SESAAT ikut email / nombor WhatsApp (baca sahaja) → { found, via, email? }. */
export const cariAkaunSesaat = (data) => panggil({ action: 'cariAkaun', data });
/** patch: { email } | { phone } (kedua-duanya rekod manual sahaja; mesti kekal sekurang-kurangnya satu) | { name } | { accountEmail } | { keJenis2: { accountEmail, hadKredit, expiresAt (ms), productId } } */
export const kemasLangganan = (c, patch) => panggil({ action: 'kemas', kind: jenisRekod(c), id: c.id, patch });
export const tandaDiingatkanLangganan = (c) => panggil({ action: 'ingat', kind: jenisRekod(c), id: c.id });
/** Padam langganan + lepaskan Gmail yang di-assign. `buangSejarah` = buang sekali resit (entry test).
 *  Destruktif, tak boleh undo. Jawapan: { ok, gmailFreed }. */
export const padamLangganan = (c, { buangSejarah = false } = {}) =>
    panggil({ action: 'padam', kind: jenisRekod(c), id: c.id, removeHistory: buangSejarah === true });

/** Langganan LAIN milik orang yang sama (uid, atau email, atau nombor WhatsApp) — untuk tanda "+1 other subscription".
 *  Satu orang boleh ada banyak, termasuk produk yang sama (keputusan user 3 Okt). */
export function langgananLain(c, customers) {
    const d = (v) => String(v || '').replace(/\D/g, '').replace(/^(?:60|0)/, '');
    const tel = d(c.phone);
    return customers.filter((x) => !(x.id === c.id && jenisRekod(x) === jenisRekod(c)) && (
        (c.uid && x.uid === c.uid) || (c.email && x.email === c.email) || (tel.length >= 7 && d(x.phone) === tel)));
}

/**
 * Pelanggan (dari muatDataFlow) + ringkasan server → pelanggan dengan `gmail` + `stage` baharu.
 *   gmail.kind: 'session' (Type 1, Session Access) | 't2' (Type 2) | 'text' (akaun ditaip tangan, rekod lama)
 * ringkasan null (gagal dimuat) → pelanggan dipulangkan tanpa diubah.
 */
export function gabungRingkasan(customers, ringkasan) {
    if (!ringkasan) return customers;
    return customers.map((c) => {
        const r = ringkasan[kunci(c)] || null;
        let gmail = null;
        if (r?.t2?.enabled) gmail = { kind: 't2', ...r.t2 };
        else if (r?.session?.enabled && r.session.assigned) gmail = { kind: 'session', ...r.session };
        else if (c.accountText) gmail = { kind: 'text', text: c.accountText };
        const tunggu = !gmail;
        const stage = tunggu ? 'unassigned'
            : c.days != null && c.days < 0 ? 'expired'
                : c.days != null && c.days <= 7 ? 'expiring' : 'active';
        return {
            ...c, stage, gmail,
            uid: c.uid || r?.uid || null,
            uidVia: r?.via || 'none',
            // Ada akses tetapi Gmail belum diletak (bayar tetapi semua Gmail penuh).
            sessionPending: !!(r?.session?.enabled && !r.session.assigned),
        };
    });
}
